import React, { useEffect, useState, useContext } from 'react';
import '../styles/ReviewModal.css';
import Review from '../interfaces/Review';
import Place from '../interfaces/Place';
import EditModalProps from '../interfaces/EditModalProps';
import GeoSearch from './GeoSearch';
import { Interface } from '../interfaces/Interface';
import dbActions from '../utils/db';
import { DocumentReference } from 'firebase/firestore';
import MyReviewsContext from '../context/MyReviewsContext';
import AppContext from '../context/AppContext';
import UserLocationReview from '../interfaces/UserLocationReview';
import Rating from '@mui/material/Rating';

const EditModal: React.FC<EditModalProps> = ({ setShowEdit, locationId, reviewId, setConfirmEdit, confirmEdit }) => {
  const [place, setPlace] = useState<Place>(Interface.emptyInterface<Place>());
  const [hasError, setHasError] = useState<boolean>(false);
  const [seeMore, setSeeMore] = useState<boolean>(false);
  const { setRefreshReviews, refreshReviews } = useContext(MyReviewsContext);
  const [hasErrorAdding, setHasErrorAdding] = useState<boolean>(false);
  const [review, setReview] = useState<Review>({
    ...Interface.emptyInterface<Review>(),
    score: 0 // Default value for score
  });
  const [locationRef, setLocationRef] = useState<DocumentReference>(Interface.emptyInterface<DocumentReference>());
  const [reviewRef, setReviewRef] = useState<DocumentReference>(Interface.emptyInterface<DocumentReference>());
  const { user } = useContext(AppContext);

  const editPlaceEntry = async() => {
    try {
      const documentExists = await checkLocationNeedsToBeDeleted();
      if (documentExists.wasModified) {
        dbActions.deleteDocument(locationRef);
        const reviewRef = await createreview(documentExists.docRef);
        await updateUserDocument(documentExists.docRef, reviewRef);
      } else {
        dbActions.updateDocument(locationRef, place);
        editReview();
      }
    } catch (error) {
      console.error("Error editing place entry: ", error);
    }
  }

  const checkLocationNeedsToBeDeleted = async (): Promise<{wasModified: boolean, docRef: DocumentReference}> => {
    const newLocationRef = await dbActions.addLocationIfNotExists(place);
    if (newLocationRef.id !== locationRef.id) {
      const reviews = await dbActions.getReviews(locationRef.id);
      if (reviews.length === 1 && reviews[0].id === reviewRef.id) {
        return {wasModified: true, docRef: newLocationRef};
      }
    }
    return {wasModified: false, docRef: locationRef};
  }

  const createreview = async (locationDocRef: DocumentReference) => {
    const reviewRef = await dbActions.addReview(locationDocRef, review);
    dbActions.deleteDocument(reviewRef);
    return reviewRef;
  }

  const editReview = () => {
    dbActions.updateDocument(reviewRef, review);
  }

  const updateUserDocument = async (locationDocRef: DocumentReference, reviewDocRef: DocumentReference) => {
    const userDoc = await dbActions.getUserFromEmail(user?.email!)
    const userData = userDoc.user;
    const updatedUserData: UserLocationReview = {...userData, locationIds: userData.locationIds.map((element) => {
      if (element.locationId === locationRef.id) {
        const reviewIds = element.reviewIds.map((reviewId) => reviewId === reviewRef.id ? reviewDocRef.id : reviewId);
        return {locationId: locationDocRef.id, reviewIds: reviewIds};
      } 
      else return element
    } 
    )}
    await dbActions.updateDocument(userDoc.docRef, updatedUserData);
  }
  
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!review.review || !review.score || review.score < 1 || review.score > 5 || !/^(1(\.[1-9])?|[2-4](\.\d)?|5(\.0)?)$/.test(review.score.toString())) {
      setHasError(true);
      return;
    }
    try {
      await editPlaceEntry();
      handleCloseModal();
      setConfirmEdit(!confirmEdit); // Update confirmEdit state here
    } catch (error) {
      setHasErrorAdding(true);
    }
  };

  const handleCloseModal = () => { 
    setRefreshReviews(!refreshReviews);
    setShowEdit(false);
  }

  const getLocation = async () => { 
    const locationData = (await dbActions.getLocation(locationId));
    setPlace(locationData.location);
    setLocationRef(locationData.docRef);
  }

  const getReview = async () => { 
    const reviewData = (await dbActions.getReview(locationId, reviewId));
    setReview(reviewData.review);
    setReviewRef(reviewData.docRef);
  }

  useEffect(() => {
    getLocation();
    getReview();
  }, [])

  return (
    <div className="modal">
      <div className="modal-content">
        <span className="close" onClick={handleCloseModal}>&times;</span>
        <h2>Editar Reseña</h2>
        <form onSubmit={handleSubmit}>
          { seeMore ||
            (<>
              <label>
                Dirección:
              </label>
              <GeoSearch setPlace={setPlace} initialPlace={place}/>
              <label>
                Dirección adicional (numero de departamento/unidad funcional, si corresponde):
              </label>
              <input 
                type='text'
                value={review.additional_details}
                onChange={(e) => setReview({...review, additional_details: e.target.value})}
              />
              <label>
                Reseña:
              </label>
              <textarea
                value={review.review} 
                required={true}
                onChange={(e) => setReview({...review, review: e.target.value})}
                placeholder='Conta tu experiencia viviendo aqui: cuanto tiempo estuviste, relación con el propietario, inmobiliaria, problemas o cosas a favor de la vivienda.'
              />
              <div className="score-input">
                <label id='scoreLabel'>Calificación</label>
                <Rating
                  name="simple-controlled"
                  value={review.score}  
                  onChange={(event, newValue) => {
                    setReview({ ...review, score: newValue!})
                  }}
                />
                {hasError && <p>Los campos dirección, reseña y calificación son requeridos.</p>}
                {hasErrorAdding && <p>Hubo un error agregando tu reseña, inténtalo de nuevo.</p>}
              </div>
              <button type="button" className='show-more-btn' onClick={() => setSeeMore(true)}>Más detalles</button>
            </>)
          }
          { seeMore &&
            (
              <>
                <div className="directOwner">
                <label>
                  Contrato por Dueño directo:
                </label>
                <input
                  type="checkbox"
                  className="custom-checkbox"
                  checked={review.directOwner || false}
                  onChange={(e) => setReview({ ...review, directOwner: e.target.checked })}
                />
              </div>
              <div className="relationshipWith">
              {review.directOwner && (
                <div className="relation-input">
                  <label>
                    Relación con Propietario:
                  </label>
                  <input
                    type="text"
                    value={review.relationshipWithOwner || ''}
                    onChange={(e) => setReview({ ...review, relationshipWithOwner: e.target.value })}
                  />
                </div>
              )}
              {!review.directOwner && (
                <div className="relation-input">
                  <label>
                    Relación con Inmobiliaria/Propietario:
                  </label>
                  <input
                    type="text"
                    value={review.relationshipWithAgent || ''}
                    onChange={(e) => setReview({ ...review, relationshipWithAgent: e.target.value })}
                  />
                </div>
              )}
              </div>
              <label>
                Comentarios Positivos:
              </label>
              <textarea
                value={review.positiveComments || ''}
                onChange={(e) => setReview({ ...review, positiveComments: e.target.value })}
                placeholder='Comparte tus comentarios positivos sobre la propiedad.'
              />
              <label>
                Comentarios Negativos:
              </label>
              <textarea
                value={review.negativeComments || ''}
                onChange={(e) => setReview({ ...review, negativeComments: e.target.value })}
                placeholder='Comparte tus comentarios negativos sobre la propiedad.'
              />
              <button type="button" className='show-more-btn' onClick={() => setSeeMore(false)}>Volver</button>
              </>
            )
          }
          <button type="submit" onClick={handleSubmit}>Enviar</button>
        </form>
      </div>
    </div>
    )
};

export default EditModal;
